import React, { useState, useEffect } from 'react';
import { useAlert } from '../context/AlertContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Plus, Trash2, Edit, Search } from 'lucide-react';
import { Table, Button, InputGroup, Form, Card, Row, Col } from 'react-bootstrap';
import { formatDateBrazilian } from '../utils/formatters';

interface Agenda {
    id: number;
    name: string;
    start_date: string;
    end_date: string;
    professional: number;
    professional_name: string;
}

const AgendaList = () => {
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const [agendas, setAgendas] = useState<Agenda[]>([]);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchAgendas();
    }, []);

    const fetchAgendas = async () => {
        const response = await api.get('/agendas/');
        setAgendas(response.data);
    };

    const handleDelete = (id: number) => {
        showAlert('Tem certeza que deseja excluir esta agenda?', {
            title: 'Confirmação',
            confirmable: true,
            confirmText: 'Excluir',
            confirmVariant: 'btn-primary',
            onConfirm: async () => {
                await api.delete(`/agendas/${id}/`);
                fetchAgendas();
            }
        });
    };

    const filteredAgendas = agendas.filter(a =>
        a.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (a.professional_name && a.professional_name.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <Card className="shadow-sm border-0">
            <Card.Body>
                <Row className="mb-4 align-items-center">
                    <Col>
                        <h3 className="mb-0">Agendas</h3>
                    </Col>
                    <Col xs="auto">
                        <Button variant="success" onClick={() => navigate('/agendas/new')} className="d-flex align-items-center gap-2">
                            <Plus size={18} /> Nova Agenda
                        </Button>
                    </Col>
                </Row>

                <InputGroup className="mb-4">
                    <InputGroup.Text className="bg-white border-end-0">
                        <Search size={18} className="text-muted" />
                    </InputGroup.Text>
                    <Form.Control
                        placeholder="Pesquisar por nome..."
                        value={searchTerm}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                        className="border-start-0 ps-0"
                    />
                </InputGroup>

                <div className="custom-table-container">
                    <Table hover className="custom-table mb-0">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>Profissional</th>
                                <th>Início</th>
                                <th>Fim</th>
                                <th className="text-end">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredAgendas.map((agenda) => (
                                <tr
                                    key={agenda.id}
                                    onClick={() => navigate(`/agendas/${agenda.id}`)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <td className="fw-medium">{agenda.name}</td>
                                    <td>{agenda.professional_name}</td>
                                    <td>{formatDateBrazilian(agenda.start_date)}</td>
                                    <td>{formatDateBrazilian(agenda.end_date)}</td>
                                    <td className="text-end">
                                        <Button
                                            variant="link"
                                            className="text-secondary p-1 me-2"
                                            onClick={(e: React.MouseEvent) => {
                                                e.stopPropagation();
                                                navigate(`/agendas/${agenda.id}`);
                                            }}
                                        >
                                            <Edit size={18} />
                                        </Button>
                                        <Button
                                            variant="link"
                                            className="text-secondary p-1"
                                            onClick={(e: React.MouseEvent) => {
                                                e.stopPropagation();
                                                handleDelete(agenda.id);
                                            }}
                                        >
                                            <Trash2 size={18} />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                            {filteredAgendas.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="text-center py-4 text-muted">
                                        Nenhuma agenda encontrada.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </Card.Body>
        </Card>
    );
};

export default AgendaList;
